import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { photosApi } from '../../../api/photos.api';
import type { ItineraryDay, TripPhoto } from '../../../types';
import { toast } from '../../ui/Toast';
import ConfirmModal from '../../ui/ConfirmModal';
import PhotoLightbox from '../../journal/PhotoLightbox';

interface Props {
  tripId: string;
  days: ItineraryDay[];
  canEdit: boolean;
  /** The stop's photos, as they were when it was opened. */
  photos: TripPhoto[];
  index: number;
  onClose: () => void;
  onReplaced: (photo: TripPhoto) => void;
  onRemoved: (photoId: string) => void;
}

/** The diary's photo viewer, opened from a stop's photos in the itinerary. */
export function StopPhotoViewer({ tripId, days, canEdit, photos: initial, index: initialIndex, onClose, onReplaced, onRemoved }: Props) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState(initial);
  const [index, setIndex] = useState(initialIndex);
  const [toDelete, setToDelete] = useState<TripPhoto | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function update(photo: TripPhoto, patch: { dayId?: string | null; activityId?: string | null }) {
    try {
      const updated = await photosApi.update(tripId, photo.id, patch);
      setPhotos(prev => prev.map(p => p.id === updated.id ? updated : p));
      onReplaced(updated);
    } catch {
      toast(t('gallery.moveError'), 'error');
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await photosApi.remove(tripId, toDelete.id);
      onRemoved(toDelete.id);
      onClose();
    } catch {
      toast(t('gallery.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <PhotoLightbox
        photos={photos}
        index={index}
        days={days}
        canEdit={canEdit}
        onIndexChange={setIndex}
        onClose={onClose}
        onMoveToDay={(photo, dayId) => update(photo, { dayId })}
        onChangeStop={(photo, activityId) => update(photo, { activityId })}
        onDelete={setToDelete}
      />
      <ConfirmModal
        open={!!toDelete}
        title={t('gallery.deleteConfirm')}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
        loading={deleting}
      />
    </>
  );
}
